import { useToast } from '@bfe/moly';
import { useEffect, useState } from 'react';

import { useI18nContext } from '@/i18n/I18n';
import { deleteAccount } from '@/services/deleteAccount';
import { IDeleteAccountErrors } from '@/services/deleteAccount/types';
import { useDeadPoolStore } from '@/store/useDeadPool';
import { useUserStore } from '@/store/useUser';
import storage from '@/utils/storage';

export const DeleteAccountPrecheckResultKey = 'delete-account-precheck';

const useDeleteAccountPrecheck = () => {
  const [$t] = useI18nContext();
  const [preChecking, setPreChecking] = useState(false);
  const [showErrorsModal, setShowErrorsModal] = useState(false);
  const [businessUnitDetails, setBusinessUnitDetails] = useState<
    IDeleteAccountErrors['businessUnitDetails']
  >([]);

  const { deadPoolConfig } = useDeadPoolStore();
  const [constants, setConstants] = useState({
    assetlimit: 5,
    registertimeinterval: 24,
    registertimes: 2,
  });

  useEffect(() => {
    try {
      const config = JSON.parse(
        deadPoolConfig?.['cht.deleteAccount.config']?.value,
      );
      setConstants((constants) => {
        return {
          assetlimit: config.assetlimit || constants.assetlimit,
          registertimeinterval:
            config.registertimeinterval || constants.registertimeinterval,
          registertimes: config.registertimes || constants.registertimes,
        };
      });
    } catch (error) {
      /* empty */
    }
  }, [deadPoolConfig]);

  const { toast, EToastType } = useToast();

  const { msgError } = useUserStore();

  /**
   * 错误原因按照rejectCode进行分组，
   * 存在同一个code对应多个翻译，最终在DeleteAccountUnableReasonModal中对同一个code进行特殊处理，拼接翻译
   */
  function deduplicateByRet(
    arr: IDeleteAccountErrors['businessUnitDetails'],
  ): IDeleteAccountErrors['businessUnitDetails'] {
    const seen = new Map<
      number,
      IDeleteAccountErrors['businessUnitDetails'][0]
    >();

    for (const item of arr) {
      if (!seen.has(item.rejectCode)) {
        seen.set(item.rejectCode, item);
      }
    }

    return Array.from(seen.values());
  }

  const formatReasons = (items: IDeleteAccountErrors[]) => {
    const reasons: IDeleteAccountErrors['businessUnitDetails'] = [];
    items?.map((item) => {
      if (item?.businessUnitDetails?.length) {
        reasons.push(...item.businessUnitDetails);
      }
    });
    setBusinessUnitDetails(deduplicateByRet(reasons));
  };

  const precheck = ({ onPass }: { onPass?: () => void }) => {
    setPreChecking(true);
    // 预检账号是否可删除
    deleteAccount({ pre_check: true })
      .then((res) => {
        if (res.isPass) {
          onPass?.();
          storage.session.set(
            DeleteAccountPrecheckResultKey,
            JSON.stringify({ [res.uid]: '1' }),
          );
        } else {
          setShowErrorsModal(true);
          formatReasons(res?.items);
        }
      })
      .catch(() => {
        toast({
          iconType: EToastType.FAIL,
          title: $t('deleteAccountFail'),
        });
      })
      .finally(() => {
        setPreChecking(false);
      });
  };

  return {
    preChecking,
    showErrorsModal,
    businessUnitDetails,
    constants,
    precheck,
    closeShowErrorsModal: () => {
      setShowErrorsModal(false);
    },
    formatReasons,
  };
};

export default useDeleteAccountPrecheck;
